'use client';

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/shared/toast';
import { useAuth } from '@/lib/auth/provider';
import { freeformThreadInvalidations } from './freeform-events';
import { NOTIFICATION_SSE_EVENT, createDebouncedInvalidator, notificationEventEffects } from './notification-events';

interface NotificationStreamContextValue {
  connected: boolean;
}

const NotificationStreamContext = createContext<NotificationStreamContextValue>({
  connected: false,
});

export function NotificationStreamProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  // `connected` is rendered through context, so it must be state — mutating a
  // ref never re-renders consumers (the old `connectedRef` was always `false`
  // to anything reading the context).
  const [connected, setConnected] = useState(false);
  const retryDelayRef = useRef(1000);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The provider wraps the whole app, the sign-in page included. With no
  // session the stream 401s, `onerror` fires, and the backoff retries it
  // forever — a signed-out tab polled the API every few seconds (UI QA pass,
  // 2026-09-24). Connect only while signed in; signing in remounts the app.
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    // What needs me refetches once per burst of notifications, not per event.
    const debounced = createDebouncedInvalidator((queryKey) => {
      queryClient.invalidateQueries({ queryKey });
    });

    function connect() {
      if (cancelled) return;
      const es = new EventSource('/api/notifications/stream');
      eventSourceRef.current = es;

      es.onopen = () => {
        setConnected(true);
        retryDelayRef.current = 1000;
      };

      // Every notification arrives as one `notification` event carrying the
      // row (the old 'DRAFT_READY' / 'NOTIFICATION' listeners never fired).
      es.addEventListener(NOTIFICATION_SSE_EVENT, (event) => {
        try {
          const effects = notificationEventEffects(JSON.parse(event.data));
          for (const queryKey of effects.invalidate) {
            queryClient.invalidateQueries({ queryKey });
          }
          debounced.add(effects.debounced);
          if (effects.toast) toast('info', effects.toast);
        } catch {
          // ignore parse errors
        }
      });

      // A new email landed on a thread (enquiry-reply threading, 2026-08-19).
      // Invalidate every email surface so open views refetch without a manual
      // refresh. The payload carries only ids; refetches go through the normal
      // authorized queries. React Query only refetches queries actually mounted.
      es.addEventListener('email_received', (event) => {
        try {
          const data = JSON.parse(event.data) as {
            scope?: string;
            inquiryId?: string;
            caseId?: string;
            clientId?: string;
          };
          if (data.scope === 'case') {
            queryClient.invalidateQueries({ queryKey: ['case-emails'] });
            queryClient.invalidateQueries({ queryKey: ['client-emails'] });
          } else {
            // enquiry (default)
            queryClient.invalidateQueries({ queryKey: ['inquiry-thread'] });
            queryClient.invalidateQueries({ queryKey: ['inquiries'] });
            if (data.inquiryId) {
              queryClient.invalidateQueries({ queryKey: ['inquiry', data.inquiryId] });
            }
          }
        } catch {
          // ignore parse errors
        }
      });

      // Freeform orchestration — the case workflow thread changed (event,
      // plan summary, proposal, auto-applied change). Ids only; refetch the
      // thread, its steps and any open artifact preview.
      es.addEventListener('freeform_thread', (event) => {
        try {
          const data = JSON.parse(event.data) as { caseId?: string };
          for (const queryKey of freeformThreadInvalidations(data)) {
            queryClient.invalidateQueries({ queryKey });
          }
        } catch {
          // ignore parse errors
        }
      });

      es.onerror = () => {
        setConnected(false);
        es.close();
        if (cancelled) return;
        const delay = retryDelayRef.current;
        retryDelayRef.current = Math.min(delay * 2, 30000);
        reconnectTimerRef.current = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      cancelled = true;
      debounced.cancel();
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      eventSourceRef.current?.close();
    };
  }, [queryClient, toast, isAuthenticated]);

  return (
    <NotificationStreamContext.Provider value={{ connected }}>
      {children}
    </NotificationStreamContext.Provider>
  );
}

export function useNotificationStream() {
  return useContext(NotificationStreamContext);
}
